import { Fragment, useMemo, useState, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, Box
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { OfferwallMetrics } from '../../../types/offerwall.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: OfferwallMetrics;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};
// 엑셀용: 통화기호 없이 숫자만
const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

export default function OfferwallRevenueTable({ data, selectedApp, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(data.dailyPublisherRevenue);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
      return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  // 합계 계산 (모든 컬럼 합계)
  const totals = useMemo(() => {
    return {
      totalRevenue: sortedData.reduce((sum, day) => sum + (day.dailyRevenue || 0), 0),
      totalBuzzvil: sortedData.reduce((sum, day) => sum + (day.buzzvil || 0), 0),
      totalIVE: sortedData.reduce((sum, day) => sum + (day.ive || 0), 0),
      totalPincrux: sortedData.reduce((sum, day) => sum + (day.pincrux || 0), 0),
      totalNAS: sortedData.reduce((sum, day) => sum + (day.nas || 0), 0),
      totalFlex: sortedData.reduce((sum, day) => sum + (day.flex || 0), 0),
      totalIGAWorks: sortedData.reduce((sum, day) => sum + (day.igaworks || 0), 0),
      totalGPA: sortedData.reduce((sum, day) => sum + (day.gpa || 0), 0),
      totalAdforus: sortedData.reduce((sum, day) => sum + (day.ADForus || 0), 0),
      totalQuantumbit: sortedData.reduce((sum, day) => sum + (day.Quantumbit2 || 0), 0),
      totalKeywordlab: sortedData.reduce((sum, day) => sum + (day.KEYWORDLAB || 0), 0),
      totalNasmedia: sortedData.reduce((sum, day) => sum + (day.nasmedia || 0), 0),
      totalMindSoftLab: sortedData.reduce((sum, day) => sum + (day.MindSoftLab || 0), 0),
      totalOhc: sortedData.reduce((sum, day) => sum + (day.OHC || 0), 0)
    };
  }, [sortedData]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((day) => {
      return [
        day.date,
        formatNumberOnly(day.dailyRevenue),
        formatNumberOnly(day.buzzvil || 0),
        formatNumberOnly(day.ive || 0),
        formatNumberOnly(day.pincrux || 0),
        formatNumberOnly(day.nas || 0),
        formatNumberOnly(day.flex || 0),
        formatNumberOnly(day.igaworks || 0),
        formatNumberOnly(day.gpa || 0),
        formatNumberOnly(day.ADForus || 0),
        formatNumberOnly(day.Quantumbit2 || 0),
        formatNumberOnly(day.KEYWORDLAB || 0),
        formatNumberOnly(day.nasmedia || 0),
        formatNumberOnly(day.MindSoftLab || 0),
        formatNumberOnly(day.OHC || 0)

      ];
    });
  }, [sortedData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="일별매출"
        />
      </Box>
      <TableContainer sx={{ backgroundColor: '#fff', borderRadius: 2, overflowX: 'auto' }}>
      <Table ref={tableRef} stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
        <TableHead>
          <TableRow sx={{ height: 36 }}>
            <TableCell sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>
              <TableSortLabel
                active
                direction={sortDirection}
                onClick={toggleSort}
              >
                날짜
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>총 매출</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Buzzvil</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>IVE</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Pincrux</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>NAS</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Flex</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>IGAWorks</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>GPA</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Adforus</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Quantumbit</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Keywordlab</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>Nasmedia</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>MindSoftLab</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>OHC</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* 합계 행 */}
          <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>합계</TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              {formatCurrency(totals.totalRevenue)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalBuzzvil)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalIVE)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalPincrux)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalNAS)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalFlex)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalIGAWorks)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalGPA)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalAdforus)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalQuantumbit)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalKeywordlab)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalNasmedia)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalMindSoftLab)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              {formatCurrency(totals.totalOhc)}
            </TableCell>
          </TableRow>
          {paged.map((day) => (
            <Fragment key={day.date}>
              {/* 날짜별 요약 행 */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{day.date}</TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                {formatCurrency(day.dailyRevenue)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.buzzvil || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.ive || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.pincrux || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.nas || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.flex || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.igaworks || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.gpa || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.ADForus || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.Quantumbit2 || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.KEYWORDLAB || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.nasmedia || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.MindSoftLab || 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {formatCurrency(day.OHC || 0)}
              </TableCell>
            </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
    </Box>
  );
}
